import type { Item, OrderForm } from 'src/sdk/checkout/entity/orderForm'
import { sendValidateCEPEvent } from 'src/utils/amplitude/validateCEPEvent'
import type { PageTypeResult } from 'src/hooks/usePageType'
import { getCookie } from 'src/utils/cookies'
import { isValidCEPSize } from 'src/utils/formatCEP'

import { axiosClient } from '../../../../services/axiosInstance'
import type { Address } from '../shipping/types'

export { isValidCEPSize }

export const isNonPersistableAddressType = (addressType?: string | null) => {
  const normalizedAddressType = addressType?.toLowerCase()

  return (
    normalizedAddressType === 'search' || normalizedAddressType === 'pickup'
  )
}

export const isMaskedPostalCode = (cep?: string | null) => {
  if (!cep) {
    return false
  }

  return cep.includes('*')
}

export const isPersistablePostalCode = (cep?: string | null) => {
  return isValidCEPSize(cep ?? undefined) && !isMaskedPostalCode(cep)
}

const formatPostalCodeWithHyphen = (postalCode: string) => {
  if (postalCode.includes('-')) {
    return postalCode
  }

  return `${postalCode.slice(0, 5)}-${postalCode.slice(5)}`
}

export const resolveJourneyPostalCode = (
  orderForm: OrderForm | null | undefined
): string | undefined => {
  const orderAddress = orderForm?.shippingData?.address

  if (
    orderAddress?.postalCode &&
    isPersistablePostalCode(orderAddress.postalCode) &&
    !isNonPersistableAddressType(orderAddress.addressType)
  ) {
    return formatPostalCodeWithHyphen(orderAddress.postalCode)
  }

  const cookiePostalCode = getCookie('postalCode')

  if (cookiePostalCode && isPersistablePostalCode(cookiePostalCode)) {
    return formatPostalCodeWithHyphen(cookiePostalCode)
  }

  const residentialAddress = findResidentialAddressIfNonResidential(
    orderForm as OrderForm
  )

  if (
    residentialAddress?.postalCode &&
    isPersistablePostalCode(residentialAddress.postalCode)
  ) {
    return formatPostalCodeWithHyphen(residentialAddress.postalCode)
  }

  return undefined
}

export function validateCPF(cpfInput: string): boolean {
  const formattedCPF = cpfInput.replace(/[^\d]/g, '')

  if (formattedCPF === '00000000000') {
    return false
  }

  let sum = 0
  let remainder

  for (let i = 1; i <= 9; i++) {
    sum += parseInt(formattedCPF.substring(i - 1, i), 10) * (11 - i)
  }

  remainder = (sum * 10) % 11

  if (remainder === 10 || remainder === 11) {
    remainder = 0
  }

  if (remainder !== parseInt(formattedCPF.substring(9, 10), 10)) {
    return false
  }

  sum = 0
  for (let i = 1; i <= 10; i++) {
    sum += parseInt(formattedCPF.substring(i - 1, i), 10) * (12 - i)
  }

  remainder = (sum * 10) % 11

  if (remainder === 10 || remainder === 11) {
    remainder = 0
  }

  if (remainder !== parseInt(formattedCPF.substring(10, 11), 10)) {
    return false
  }

  return true
}

export const maskCPF = (cpf: string | number): string => {
  const cpfString = String(cpf).replace(/[^\d]/g, '')

  if (cpfString.length !== 11) {
    return String(cpf)
  }

  return `${cpfString.slice(0, 3)}.${cpfString.slice(3, 6)}.${cpfString.slice(
    6,
    9
  )}-${cpfString.slice(9, 11)}`
}

export const maskPhone = (phone: string): string => {
  const cleanedPhone = phone.startsWith('+') ? phone.slice(3) : phone

  if (cleanedPhone.length < 10 || cleanedPhone.length > 11) {
    return String(phone)
  }

  return cleanedPhone.length === 10
    ? `(${cleanedPhone.slice(0, 2)}) ${cleanedPhone.slice(
        2,
        6
      )}-${cleanedPhone.slice(6)}`
    : `(${cleanedPhone.slice(0, 2)}) ${cleanedPhone.slice(
        2,
        7
      )}-${cleanedPhone.slice(7)}`
}

export const unmaskCEP = (cep: string) => {
  return cep?.replace(/\D/g, '')
}

export const formatAddress = (
  street?: string,
  city?: string,
  state?: string
) => {
  let result = ''

  if (street) {
    result += street
  }

  if (street && city) {
    result += ' - '
  }

  if (city) {
    result += city
  }

  if (city && state) {
    result += ' ('
  }

  if (state) {
    result += state
  }

  if (city && state) {
    result += ')'
  }

  return result
}

export const checkIfHasUserProfileData = (orderForm: OrderForm) => {
  const { clientProfileData } = orderForm ?? {}

  const {
    document,
    firstName,
    lastName,
    phone,
    email: userEmail,
  } = clientProfileData ?? {}

  return !!(firstName && lastName && document && phone && userEmail)
}

export const findResidentialAddressIfNonResidential = (
  orderForm: OrderForm
) => {
  const isAddressResidential =
    orderForm?.shippingData?.address?.addressType === 'residential'

  if (isAddressResidential) {
    return null
  }

  const filteredAddresses = orderForm?.shippingData?.availableAddresses?.filter(
    (address: Address) => address.addressType === 'residential'
  )

  return filteredAddresses?.[filteredAddresses.length - 1] ?? null
}

export const resolveResidentialShippingAddress = (orderForm: OrderForm) => {
  const currentAddress = orderForm?.shippingData?.address

  if (
    currentAddress &&
    !isNonPersistableAddressType(currentAddress.addressType)
  ) {
    return currentAddress
  }

  return findResidentialAddressIfNonResidential(orderForm)
}

export function isValidAddress(address: Address | undefined | null): boolean {
  if (!address) {
    return false
  }

  // Endereço do tipo "search" não possui receiverName
  if (address.addressType === 'search') {
    return true
  }

  return (
    !!address.postalCode &&
    !!address.number &&
    !!address.neighborhood &&
    !!address.city &&
    !!address.state &&
    !!address.receiverName
  )
}

export const buildAddressObject = (
  dataAddress: Address | null,
  validCep: string,
  addressId: string | undefined
): Address => ({
  addressType: 'residential',
  receiverName: dataAddress?.receiverName ?? '',
  postalCode: validCep ? unmaskCEP(validCep) : '',
  city: dataAddress?.city ?? '',
  state: dataAddress?.state ?? '',
  country: 'BRA',
  street: dataAddress?.street ?? '',
  number: dataAddress?.number ?? '',
  neighborhood: dataAddress?.neighborhood ?? '',
  complement: dataAddress?.complement ?? '',
  addressId: addressId ?? '',
})

type SetErrorFn = (error: { type: string; message: string }) => void

export const validateCep = (
  cep: string | undefined,
  setError: SetErrorFn
): boolean => {
  if (!isValidCEPSize(cep)) {
    setError({ type: 'cep', message: 'CEP não encontrado' })

    return false
  }

  return true
}

export const getCEP = async ({
  cep,
  shouldSendEvent = false,
  orderForm,
  pageData,
}: {
  cep: string
  shouldSendEvent?: boolean
  orderForm?: OrderForm
  pageData: PageTypeResult
}): Promise<Address | null> => {
  try {
    const { data }: { data: Address } = await axiosClient.get(
      `/api/getAddressByPostalCode?cep=${cep}`
    )

    if (shouldSendEvent && orderForm) {
      sendValidateCEPEvent({
        orderForm,
        isCEPValid: true,
        pageData,
      })
    }

    return data
  } catch {
    return null
  }
}

export const isNextDay = (items?: Item[] | null): boolean => {
  return (
    items?.some((item) =>
      Boolean(
        item?.deliveryTypes?.some((delivery) => delivery.type === 'next-day')
      )
    ) ?? false
  )
}

export {
  getCartTotalValue,
  getCartItemsCount,
  getCartItemsQuantity,
} from 'src/utils/amplitude/cartEventValues'
